import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Lock, Search, Download, MapPin, Calendar as CalIcon, Phone, Mail, Users, ExternalLink, Trash2, LogOut, Activity, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'mistika_admin_pw';

const cityIcons = { bogota: '🏛️', medellin: '🌸', cali: '☀️' };

export default function AdminDashboard() {
  const [password, setPassword] = useState(localStorage.getItem(STORAGE_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState({ city: '', search: '', from: '', to: '' });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (password) tryAuth(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryAuth = async (pw) => {
    setLoading(true);
    setError('');
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/admin/appointments`, { params: { password: pw } }),
        axios.get(`${API}/admin/stats`, { params: { password: pw } }),
      ]);
      setAppointments(a.data);
      setStats(s.data);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, pw);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al cargar');
      setAuthed(false);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  };

  const reload = () => password && tryAuth(password);

  const filtered = useMemo(() => {
    return appointments.filter((a) => {
      if (filter.city && a.city !== filter.city) return false;
      if (filter.from && a.date < filter.from) return false;
      if (filter.to && a.date > filter.to) return false;
      if (filter.search) {
        const q = filter.search.toLowerCase();
        const blob = `${a.name} ${a.phone} ${a.email || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [appointments, filter]);

  const exportCSV = () => {
    const rows = [
      ['ID', 'Nombre', 'Teléfono', 'Email', 'Ciudad', 'Sede', 'Dirección', 'Fecha', 'Hora', 'Event ID', 'Creado'],
      ...filtered.map((a) => [
        a.id, a.name, a.phone, a.email || '', a.city_name || a.city, '', a.address || '',
        a.date, a.time, a.event_id || '', a.created_at || ''
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `citas-mistika-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
  };

  const deleteAppt = async (id) => {
    if (!window.confirm('¿Eliminar esta cita? Se borrará también del calendario.')) return;
    try {
      await axios.delete(`${API}/admin/appointments/${id}`, { params: { password } });
      setSelected(null);
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPassword('');
    setAuthed(false);
    setAppointments([]);
  };

  if (!authed) {
    return (
      <div className="hero-bg stars min-h-screen flex items-center justify-center px-4">
        <div className="quiz-dark p-8 sm:p-10 w-full max-w-md" data-testid="admin-login">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-purple-500/20 border border-fuchsia-400/40 flex items-center justify-center neon-pulse">
              <Lock className="w-6 h-6 text-fuchsia-300" />
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold text-white text-center mb-1">Panel Administrador</h1>
          <p className="text-purple-200/70 text-center text-sm mb-6">Mistika · Citas y agendamientos</p>
          <input
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && tryAuth(password)}
            className="purple-input-dark mb-3"
            data-testid="admin-password-input"
            autoFocus
          />
          {error && <p className="text-red-300 text-sm mb-3 text-center">{error}</p>}
          <button onClick={() => tryAuth(password)} disabled={!password || loading} className="cta-glow w-full" data-testid="admin-login-button">
            {loading ? 'Verificando…' : 'Ingresar →'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-bg stars min-h-screen px-3 sm:px-6 py-6 sm:py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-purple-300/70 font-bold">Mistika · Admin</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">Panel de Agendamientos</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={reload} className="chip-dark hover:!bg-purple-500/30 transition" data-testid="admin-reload">↻ Recargar</button>
            <button onClick={exportCSV} className="chip-dark hover:!bg-purple-500/30 transition flex items-center gap-1.5" data-testid="admin-export"><Download className="w-3.5 h-3.5" /> CSV</button>
            <button onClick={logout} className="chip-dark hover:!bg-red-500/30 transition flex items-center gap-1.5" data-testid="admin-logout"><LogOut className="w-3.5 h-3.5" /> Salir</button>
          </div>
        </header>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
            <StatCard icon={<Users className="w-5 h-5" />} label="Total citas" value={stats.total} color="#a855f7" />
            <StatCard icon={<CalIcon className="w-5 h-5" />} label="Próximas" value={stats.upcoming} color="#d946ef" />
            <StatCard icon={<Activity className="w-5 h-5" />} label="Ciudades activas" value={Object.keys(stats.by_city || {}).length} color="#f472b6" />
            <StatCard icon={<MapPin className="w-5 h-5" />} label="Top ciudad" value={topCity(stats.by_city)} color="#60a5fa" />
          </div>
        )}

        {/* Filters */}
        <div className="quiz-dark p-4 mb-4" data-testid="admin-filters">
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="relative sm:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-300/60" />
              <input
                type="text"
                placeholder="Buscar por nombre, teléfono o email…"
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                className="purple-input-dark !pl-9"
                data-testid="admin-search"
              />
            </div>
            <select
              value={filter.city}
              onChange={(e) => setFilter({ ...filter, city: e.target.value })}
              className="purple-input-dark"
              data-testid="admin-city-filter"
            >
              <option value="">Todas las ciudades</option>
              <option value="bogota">Bogotá</option>
              <option value="medellin">Medellín</option>
              <option value="cali">Cali</option>
            </select>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} className="purple-input-dark !text-xs" data-testid="admin-from" />
              <input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} className="purple-input-dark !text-xs" data-testid="admin-to" />
            </div>
          </div>
          <p className="text-purple-300/60 text-xs mt-3">
            Mostrando <strong className="text-fuchsia-300">{filtered.length}</strong> de {appointments.length} citas
          </p>
        </div>

        {/* Table */}
        <div className="quiz-dark overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-purple-300/70 font-bold border-b border-purple-700/40">
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Ciudad</th>
                  <th className="px-4 py-3">Fecha · Hora</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-10 text-purple-300/60">Sin citas que coincidan con los filtros</td></tr>
                ) : filtered.map((a) => (
                  <tr key={a.id} className="border-b border-purple-700/20 hover:bg-purple-500/10 transition" data-testid={`row-${a.id}`}>
                    <td className="px-4 py-3 font-semibold text-white">{a.name}</td>
                    <td className="px-4 py-3 text-purple-200/85">
                      <div className="flex items-center gap-1.5 text-xs"><Phone className="w-3 h-3 text-fuchsia-300" />{a.phone}</div>
                      {a.email && <div className="flex items-center gap-1.5 text-xs mt-0.5 text-purple-300/65"><Mail className="w-3 h-3" />{a.email}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="chip-dark !py-1">{cityIcons[a.city]} {a.city_name}</span>
                    </td>
                    <td className="px-4 py-3 text-purple-100/90">
                      <div className="font-semibold">{a.date}</div>
                      <div className="text-fuchsia-300 text-xs">{fmt12(a.time)}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => setSelected(a)} className="px-2 py-1 rounded-lg bg-purple-500/20 hover:bg-purple-500/40 text-fuchsia-200 text-xs flex items-center gap-1 transition" data-testid={`view-${a.id}`}>
                          <Eye className="w-3.5 h-3.5" /> Ver
                        </button>
                        {a.event_link && (
                          <a href={a.event_link} target="_blank" rel="noreferrer" className="px-2 py-1 rounded-lg bg-purple-500/20 hover:bg-purple-500/40 text-fuchsia-200 text-xs flex items-center gap-1 transition" data-testid={`gcal-${a.id}`}>
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button onClick={() => deleteAppt(a.id)} className="px-2 py-1 rounded-lg bg-red-500/15 hover:bg-red-500/30 text-red-300 text-xs flex items-center gap-1 transition" data-testid={`del-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-6" onClick={() => setSelected(null)}>
          <div className="quiz-dark max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-purple-300/70 font-bold">Detalle de cita</p>
                <h2 className="font-display text-2xl font-bold text-white">{selected.name}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-purple-300/70 hover:text-white text-xl">✕</button>
            </div>
            <div className="space-y-2 text-sm text-purple-100">
              <Detail label="Teléfono" value={selected.phone} />
              {selected.email && <Detail label="Email" value={selected.email} />}
              <Detail label="Ciudad" value={`${cityIcons[selected.city]} ${selected.city_name}`} />
              <Detail label="Dirección" value={selected.address} />
              <Detail label="Fecha" value={selected.date} />
              <Detail label="Hora" value={fmt12(selected.time)} />
              <Detail label="Creada" value={selected.created_at?.slice(0, 19).replace('T', ' ')} />
            </div>
            {selected.answers && Object.keys(selected.answers).length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] tracking-[0.3em] uppercase text-purple-300/70 font-bold mb-2">Respuestas del quiz</p>
                <div className="space-y-1 text-sm">
                  {Object.entries(selected.answers).map(([k, v]) => (
                    <div key={k} className="urg-box-dark !py-2">
                      <span className="text-purple-300/70 text-xs">{labelize(k)}:</span>{' '}
                      <span className="text-white">{Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? Object.entries(v).map(([kk, vv]) => `${kk}: ${vv}`).join(' · ') : String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 mt-5">
              {selected.event_link && (
                <a href={selected.event_link} target="_blank" rel="noreferrer" className="cta-glow flex-1 flex items-center justify-center gap-2 !py-2.5">
                  <ExternalLink className="w-4 h-4" /> Ver en Calendar
                </a>
              )}
              <button onClick={() => deleteAppt(selected.id)} className="px-4 py-2.5 rounded-full bg-red-500/20 hover:bg-red-500/35 border border-red-400/40 text-red-200 text-sm font-semibold transition" data-testid="modal-delete">
                <Trash2 className="w-4 h-4 inline mr-1" /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="quiz-dark p-4">
      <div className="flex items-center gap-2 mb-2" style={{ color }}>
        {icon}
        <span className="text-[10px] uppercase tracking-wider font-bold text-purple-200/80">{label}</span>
      </div>
      <p className="font-display text-2xl sm:text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-purple-700/20 last:border-b-0">
      <span className="text-purple-300/70 text-xs uppercase tracking-wider">{label}</span>
      <span className="text-white text-right">{value}</span>
    </div>
  );
}

function fmt12(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const am = h < 12;
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${am ? 'AM' : 'PM'}`;
}

function topCity(byCity) {
  if (!byCity) return '—';
  const entries = Object.entries(byCity);
  if (!entries.length) return '—';
  entries.sort((a, b) => b[1] - a[1]);
  const names = { bogota: 'Bogotá', medellin: 'Medellín', cali: 'Cali' };
  return names[entries[0][0]] || entries[0][0];
}

function labelize(k) {
  const labels = {
    tratamiento_previo: 'Tratamiento previo',
    zona: 'Zona',
    morfologia: 'Morfología',
    datos: 'Datos físicos',
    frecuencia: 'Frecuencia',
    inversion: 'Inversión',
    interes: 'Interés',
  };
  return labels[k] || k;
}
